/**
 * useCall — Phase 2 Task 7 + Phase 3 Track A (screen-share) + camera.
 *
 * A focused media-join hook. Once the CallProvider has negotiated a room
 * (via ring→accept or accept→connecting), CallWindow calls `joinRoom` to
 * actually connect to the conferweb media server.
 *
 * API:
 *   joinRoom(room, displayName)   — auth + getUserMedia(audio) + media join
 *   hangup()                      — end ALL sessions + stop ALL local tracks
 *   state                         — "idle"|"connecting"|"connected"|"ended"|"error"
 *   connectionState               — live RTCIceConnectionState of the base call
 *   remoteStream                  — the remote audio MediaStream once connected
 *
 *   startScreenShare()            — getDisplayMedia → shareScreen, returns ownSessionId or null
 *   stopScreenShare()             — terminate screen publish session + stop tracks
 *   viewScreen(sharerSessionId)   — subscribe to a peer's screen share (receive-only)
 *   stopViewScreen()              — terminate the view session
 *
 *   enableCamera()                — getUserMedia(video) → (optional background fx) → shareCamera
 *   disableCamera()               — terminate camera publish + stop tracks + fx processor
 *   viewCamera(sharerSessionId)   — subscribe to the peer's camera (receive-only)
 *   stopViewCamera()              — terminate the camera view session
 *
 *   backgroundMode                — current camera background ('none'|'blur'|preset id)
 *   setBackgroundMode(mode)       — change/persist the background live; returns
 *                                   'unsupported' if an effect couldn't init (stays raw)
 *
 * Background effects are ENTIRELY ADDITIVE: 'none' publishes the raw camera
 * untouched, and any failure to load the segmentation model falls back to the
 * raw camera — a background effect never drops the call. See media/backgroundFx.ts.
 *
 *   canShareScreen()              — pure helper; true if getDisplayMedia is available
 *
 * Every publish/view path is guarded against double-invoke (a React re-render or
 * StrictMode double-effect must not open a second SFU session → leaked ports).
 * Every session terminate also stops its JsSIP UA (see mediaClient.placeCall) so
 * the SFU frees the port. See docs/calls.md for the three-plane architecture.
 */
import { useCallback, useRef, useState } from 'react';
import { authCall } from './api.js';
import { createMediaClient } from './media/mediaClient.js';
import {
  createBackgroundProcessor,
  isEffectMode,
  loadBackgroundPref,
  saveBackgroundPref,
} from './media/backgroundFx.js';
import {
  deviceConstraint,
  isDefaultDeviceId,
  loadDevicePref,
  saveDevicePref,
} from './media/devicePrefs.js';
import type { CallRoomCoords } from '@xop/dashboard-shared';
import type { MediaClient, MediaSession } from './media/types.js';
import type { BackgroundMode, BackgroundProcessor } from './media/backgroundFx.js';

export type UseCallState = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

export interface UseCallOptions {
  /** Called once the remote stream starts flowing (after joinRoom succeeds). */
  onConnected?: () => void;
  /** Called when the base call drops at the MEDIA layer WITHOUT a local hangup()
   *  — i.e. the server terminated the conference or the peer's media died. Lets
   *  the provider close the call window + post the call record. NOT fired for a
   *  user-initiated hangup() (the provider already knows in that case). */
  onMediaClosed?: () => void;
  /** Called with our conferweb SFU session id (`accountData.sid`) once the room
   * auth resolves — the provider reports it to the server so peers can subscribe
   * to our camera (group video). */
  onSid?: (sid: number) => void;
}

// ---------------------------------------------------------------------------
// Pure capability helper — exported so CallWindow can gate the Share button.
// ---------------------------------------------------------------------------
export function canShareScreen(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useCall(options?: UseCallOptions) {
  const [state, setState] = useState<UseCallState>('idle');
  const [connectionState, setConnectionState] = useState<RTCIceConnectionState | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  // The local mic stream, exposed so the UI can render a live audio-level meter.
  const [localAudioStream, setLocalAudioStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [localScreenStream, setLocalScreenStream] = useState<MediaStream | null>(null);
  const [remoteScreenStream, setRemoteScreenStream] = useState<MediaStream | null>(null);
  // Camera (audio-first: not on until enableCamera()). cameraOn = WE are
  // publishing our camera; localCameraStream is our preview; remoteCameraStream
  // is the peer's camera once we viewCamera() it.
  const [cameraOn, setCameraOn] = useState(false);
  const [localCameraStream, setLocalCameraStream] = useState<MediaStream | null>(null);
  // Remote cameras keyed by the publisher's conferweb sid. A group call has one
  // receive-only view leg per remote camera, so this is a MAP (not a single peer).
  // For a 1:1 call it simply holds at most one entry.
  const [remoteCameras, setRemoteCameras] = useState<Map<number, MediaStream>>(new Map());
  // Background effect applied to the LOCAL camera (blur / virtual background).
  // 'none' = the raw camera (default, byte-identical to the pre-feature path).
  // Persisted so it carries across calls.
  const [backgroundMode, setBackgroundModeState] = useState<BackgroundMode>(() => loadBackgroundPref());
  // True while a background effect is being built/applied (the first-time model
  // load takes a moment) — the UI shows a spinner so it's clear what's happening.
  const [backgroundBusy, setBackgroundBusy] = useState(false);

  // Main call session
  const sessionRef = useRef<MediaSession | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  // Set true the moment WE initiate hangup() so the base-call onEnded/onFailure
  // doesn't also fire onMediaClosed (which would double-report the end).
  const selfHangupRef = useRef(false);

  // Retained auth + room refs — populated after joinRoom so the screen methods
  // can reference them without extra params.
  const authRef = useRef<{
    sessionId: number;
    meetingId: number;
    conferenceId: number;
  } | null>(null);
  // The webcast session token from authCall — proves membership in this room's
  // conference. Forwarded (server-side) to C1's dial_out to add an external
  // phone to the call. Exposed so the keypad can dial. Null until joined.
  const [webcastToken, setWebcastToken] = useState<string | null>(null);
  const roomRef = useRef<CallRoomCoords | null>(null);
  const displayNameRef = useRef<string>('');
  const clientRef = useRef<MediaClient | null>(null);

  // Screen-share sessions
  const screenShareSessionRef = useRef<MediaSession | null>(null);
  const screenTracksRef = useRef<MediaStreamTrack[]>([]);
  const viewSessionRef = useRef<MediaSession | null>(null);
  // Track the ownSessionId used when screen-share started (for signalScreenShare stop)
  const sharingSessionIdRef = useRef<number | null>(null);

  // Camera sessions (publish my camera + view the peer's), mirroring screen.
  const cameraSessionRef = useRef<MediaSession | null>(null);
  const cameraTracksRef = useRef<MediaStreamTrack[]>([]);
  // One receive-only camera view session per remote sid (group all-to-all mesh).
  const viewCameraSessionsRef = useRef<Map<number, MediaSession>>(new Map());
  // The unprocessed getUserMedia camera (the segmentation source). With an
  // effect active, `cameraTracksRef` holds the PROCESSED canvas track we publish
  // and this holds the raw camera, so we can swap effects (or fall back to raw)
  // without re-acquiring the device. `bgProcessorRef` is the live effect, if any.
  const rawCameraStreamRef = useRef<MediaStream | null>(null);
  const bgProcessorRef = useRef<BackgroundProcessor | null>(null);
  // Mirror of backgroundMode for reads inside callbacks without a stale closure.
  const backgroundModeRef = useRef<BackgroundMode>(backgroundMode);
  // The background mode currently REALISED in the published track (it can lag
  // `backgroundModeRef` while a switch is mid-flight). `bgReconcilingRef` is a
  // single-flight guard so rapid effect switching can't spawn concurrent
  // segmentation processors and orphan one (rAF loop + segmenter leak).
  const appliedBgModeRef = useRef<BackgroundMode>('none');
  const bgReconcilingRef = useRef(false);
  // Preferred input devices (chosen in the call settings menu); applied to new
  // getUserMedia calls + hot-swapped onto live sessions. Seeded from localStorage
  // so a manual choice is remembered across calls / app restarts; `null` = follow
  // the OS default device (the requirement: default mic + speaker unless the user
  // switched). See media/devicePrefs.ts.
  const preferredAudioDeviceIdRef = useRef<string | null>(loadDevicePref('audioinput'));
  const preferredVideoDeviceIdRef = useRef<string | null>(loadDevicePref('videoinput'));

  // In-flight guards — a session ref is only assigned AFTER its async setup
  // resolves, so a second synchronous call (re-render / StrictMode) would see a
  // null ref and open a duplicate SFU session. These close that window.
  const screenPendingRef = useRef(false);
  const viewScreenPendingRef = useRef(false);
  const cameraPendingRef = useRef(false);
  // Per-sid in-flight guard so a synchronous re-subscribe to the same peer (a
  // re-render / StrictMode double-call) opens exactly one view leg.
  const viewCameraPendingRef = useRef<Set<number>>(new Set());

  /** Stop all tracks on the local getUserMedia stream and clear the ref. */
  const stopLocalStream = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  }, []);

  /**
   * Acquire the base-call microphone, honouring a remembered device but
   * gracefully falling back to the OS default if that device has since
   * disappeared (unplugged / no longer permitted). A real "no mic at all"
   * failure propagates to the caller (→ error state).
   */
  const acquireMic = useCallback(async (): Promise<MediaStream> => {
    const preferred = preferredAudioDeviceIdRef.current;
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: deviceConstraint(preferred),
        video: false,
      });
    } catch (err) {
      if (isDefaultDeviceId(preferred)) throw err; // already default → real failure
      // The remembered mic is gone → forget it and fall back to the OS default.
      preferredAudioDeviceIdRef.current = null;
      saveDevicePref('audioinput', null);
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    }
  }, []);

  /** Stop the local screen tracks + clear the self-preview. */
  const stopScreenTracks = useCallback(() => {
    screenTracksRef.current.forEach((t) => t.stop());
    screenTracksRef.current = [];
    setLocalScreenStream(null);
  }, []);

  /** Stop ALL local camera media — the published track(s), the background
   *  processor, and the raw camera source — and clear their refs. */
  const stopCameraMedia = useCallback(() => {
    bgProcessorRef.current?.stop();
    bgProcessorRef.current = null;
    cameraTracksRef.current.forEach((t) => {
      t.onended = null;
      t.stop();
    });
    cameraTracksRef.current = [];
    rawCameraStreamRef.current?.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
    });
    rawCameraStreamRef.current = null;
    appliedBgModeRef.current = 'none';
  }, []);

  const joinRoom = useCallback(async (room: CallRoomCoords, displayName: string) => {
    try {
      selfHangupRef.current = false;
      setConnectionState(null);
      setMuted(false);
      setState('connecting');
      const auth = await authCall(String(room.roomId), room.participantPin, displayName);

      // Retain context FIRST (auth + room + media client) so the camera-view
      // effects can actually open a view leg. ORDER MATTERS: a LATE
      // joiner already has a camera to watch at join time, so syncCameraViews
      // fires the instant `webcastToken` is set (and again when get_attendees
      // discovery resolves). If `clientRef`/`authRef`/`roomRef` aren't set yet,
      // viewCamera() bails on its `if (!client) return` guard and NEVER retries —
      // the late joiner stays on `viewing=[]` and never sees the camera. So set
      // the refs (incl. the media client) BEFORE publishing `webcastToken`.
      authRef.current = {
        sessionId: auth.sessionId,
        meetingId: auth.meetingId,
        conferenceId: auth.conferenceId,
      };
      roomRef.current = room;
      displayNameRef.current = displayName;

      // Audio-only base call — the camera is NOT acquired at call start. The
      // camera is turned on later via enableCamera() (a separate SFU session).
      // Uses the remembered mic (or the OS default when unset).
      const localStream = await acquireMic();
      localStreamRef.current = localStream;

      const client = createMediaClient({
        mediaWsUrl: room.mediaWsUrl,
        sipDomain: room.sipDomain,
        iceServers: auth.iceServers,
      });
      clientRef.current = client;

      // NOW publish the auth-derived state. `webcastToken` drives the
      // get_attendees discovery + camera-view reconcile effects — which now find
      // clientRef/authRef/roomRef ready (set above), so a late joiner subscribes.
      setLocalAudioStream(localStream);
      setWebcastToken(auth.token);
      // Publish our SFU sid so the server adds it to our roster entry — peers use
      // it to subscribe to our camera (group all-to-all video).
      options?.onSid?.(auth.sessionId);

      sessionRef.current = await client.join(
        {
          sessionId: auth.sessionId,
          meetingId: auth.meetingId,
          conferenceId: auth.conferenceId,
          participantPin: room.participantPin,
          displayName,
          localStream,
        },
        {
          onRemoteStream: (s) => {
            setRemoteStream(s);
            setState('connected');
            options?.onConnected?.();
          },
          onConnectionState: (s) => setConnectionState(s),
          onEnded: () => {
            stopLocalStream();
            setState('ended');
            // The base SIP call dropped. If WE didn't initiate it, the server
            // terminated the conference (or the peer vanished) — tell the
            // provider so the window closes + the call record posts.
            if (!selfHangupRef.current) options?.onMediaClosed?.();
          },
          onFailure: () => {
            stopLocalStream();
            setState('error');
            if (!selfHangupRef.current) options?.onMediaClosed?.();
          },
        },
      );
    } catch {
      stopLocalStream();
      setState('error');
    }
  }, [options, stopLocalStream, acquireMic]);

  // ---------------------------------------------------------------------------
  // joinConsultation — a FRESH outbound PSTN call (the launcher's call button),
  // ported from H&H consultation. We /auth into the pre-provisioned ephemeral
  // room (for the SFU session + moderator routing context), then place a DIRECT
  // JsSIP INVITE to the phone NUMBER (not a room-join) — C1 originates the PSTN
  // leg and bridges it. Mirrors joinRoom's lifecycle/handlers so CallWindow drives
  // it identically (connecting → connected → ended). Requires room.moderatorPin;
  // CallWindow falls back to joinRoom + dial_out when it's absent.
  // ---------------------------------------------------------------------------
  const joinConsultation = useCallback(async (room: CallRoomCoords, number: string, displayName: string) => {
    try {
      selfHangupRef.current = false;
      setConnectionState(null);
      setMuted(false);
      setState('connecting');
      const auth = await authCall(String(room.roomId), room.participantPin, displayName);
      authRef.current = {
        sessionId: auth.sessionId,
        meetingId: auth.meetingId,
        conferenceId: auth.conferenceId,
      };
      roomRef.current = room;
      displayNameRef.current = displayName;

      const localStream = await acquireMic();
      localStreamRef.current = localStream;

      const client = createMediaClient({
        mediaWsUrl: room.mediaWsUrl,
        sipDomain: room.sipDomain,
        iceServers: auth.iceServers,
      });
      clientRef.current = client;

      setLocalAudioStream(localStream);
      setWebcastToken(auth.token);
      options?.onSid?.(auth.sessionId);

      sessionRef.current = await client.consultationDial(
        {
          sessionId: auth.sessionId,
          conferenceId: auth.conferenceId,
          moderatorPin: room.moderatorPin ?? '',
          number,
          displayName,
          localStream,
        },
        {
          // Bind the remote audio so the answered party is audible — but do NOT
          // mark the call connected here. Our leg to C1 comes up immediately (C1
          // answers our INVITE to bridge), BEFORE the dialed party is off-hook;
          // CallWindow's answer-poll flips to connected on real pickup. Until then
          // the caller hears ringback (the call stays in `ringing`).
          onRemoteStream: (s) => { setRemoteStream(s); },
          onConnectionState: (s) => setConnectionState(s),
          onEnded: () => {
            stopLocalStream();
            setState('ended');
            if (!selfHangupRef.current) options?.onMediaClosed?.();
          },
          onFailure: () => {
            stopLocalStream();
            setState('error');
            if (!selfHangupRef.current) options?.onMediaClosed?.();
          },
        },
      );
    } catch {
      stopLocalStream();
      setState('error');
    }
  }, [options, stopLocalStream, acquireMic]);

  // ---------------------------------------------------------------------------
  // startScreenShare
  // ---------------------------------------------------------------------------
  const startScreenShare = useCallback(async (): Promise<number | null> => {
    const auth = authRef.current;
    const room = roomRef.current;
    const client = clientRef.current;
    if (!auth || !room || !client) return null;
    // Dedup: already sharing or a share is mid-setup.
    if (screenShareSessionRef.current || screenPendingRef.current) return null;
    screenPendingRef.current = true;

    try {
      let rawStream: MediaStream;
      try {
        rawStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      } catch {
        // User cancelled or permission denied — return null gracefully
        return null;
      }

      // Build a video-only stream
      const videoTracks = rawStream.getVideoTracks();
      const videoOnly = new MediaStream(videoTracks);
      screenTracksRef.current = videoTracks;
      setLocalScreenStream(videoOnly); // self-preview of what I'm sharing

      let legFailed = false;
      const session = await client.shareScreen(
        {
          ownSessionId: auth.sessionId,
          meetingId: auth.meetingId,
          conferenceId: auth.conferenceId,
          participantPin: room.participantPin,
          displayName: displayNameRef.current,
          screenStream: videoOnly,
        },
        {
          onRemoteStream: () => { /* publisher: no remote stream expected */ },
          onEnded: () => {
            legFailed = true;
            stopScreenTracks();
            setIsSharing(false);
            sharingSessionIdRef.current = null;
          },
          onFailure: () => {
            legFailed = true;
            stopScreenTracks();
            setIsSharing(false);
            sharingSessionIdRef.current = null;
          },
        },
      );

      // Publish leg failed terminally (after retries) → peers would see nothing.
      // Don't claim we're sharing; return null so the caller toasts it. Same if
      // the call ended while this leg was setting up (retry widened that window).
      if (legFailed || selfHangupRef.current) {
        session.hangup();
        if (selfHangupRef.current) stopScreenTracks();
        return null;
      }

      screenShareSessionRef.current = session;
      sharingSessionIdRef.current = auth.sessionId;
      setIsSharing(true);

      // Wire the browser's native "Stop sharing" button so it tears down cleanly.
      videoTracks.forEach((t) => {
        t.onended = () => {
          screenShareSessionRef.current?.hangup();
          screenShareSessionRef.current = null;
          stopScreenTracks();
          setIsSharing(false);
          sharingSessionIdRef.current = null;
        };
      });

      return auth.sessionId;
    } finally {
      screenPendingRef.current = false;
    }
  }, [stopScreenTracks]);

  // ---------------------------------------------------------------------------
  // stopScreenShare
  // ---------------------------------------------------------------------------
  const stopScreenShare = useCallback(() => {
    screenShareSessionRef.current?.hangup();
    screenShareSessionRef.current = null;
    stopScreenTracks();
    setIsSharing(false);
    sharingSessionIdRef.current = null;
  }, [stopScreenTracks]);

  // ---------------------------------------------------------------------------
  // viewScreen — receive-only subscribe to a peer's screen share.
  // ---------------------------------------------------------------------------
  const viewScreen = useCallback(async (sharerSessionId: number) => {
    const auth = authRef.current;
    const room = roomRef.current;
    const client = clientRef.current;
    if (!auth || !room || !client) return;
    // Never subscribe to OUR OWN screen (defense-in-depth against self-view).
    if (sharerSessionId === auth.sessionId) return;
    // Dedup: already viewing or a view is mid-setup (StrictMode double-effect).
    if (viewSessionRef.current || viewScreenPendingRef.current) return;
    viewScreenPendingRef.current = true;

    let legFailed = false;
    try {
      const session = await client.viewScreen(
        {
          sharerSessionId,
          ownSessionId: auth.sessionId,
          meetingId: auth.meetingId,
          conferenceId: auth.conferenceId,
          participantPin: room.participantPin,
          displayName: displayNameRef.current,
        },
        {
          onRemoteStream: (s) => setRemoteScreenStream(s),
          onEnded: () => { legFailed = true; setRemoteScreenStream(null); },
          onFailure: () => { legFailed = true; /* silent — peer may have stopped sharing */ },
        },
      );

      // Don't record a dead leg (after placeCall's retries) — leave the ref null
      // so a re-trigger can retry rather than no-op on a stale "viewing" session.
      // Same for a hangup that landed while this leg was still setting up.
      if (legFailed || selfHangupRef.current) session.hangup();
      else viewSessionRef.current = session;
    } finally {
      viewScreenPendingRef.current = false;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // stopViewScreen
  // ---------------------------------------------------------------------------
  const stopViewScreen = useCallback(() => {
    // Reset the in-flight flag too — else a stop during mid-setup leaves it true
    // and every later viewScreen() no-ops (peer re-shares → nothing appears).
    viewScreenPendingRef.current = false;
    viewSessionRef.current?.hangup();
    viewSessionRef.current = null;
    setRemoteScreenStream(null);
  }, []);

  // ---------------------------------------------------------------------------
  // enableCamera — publish the local camera as a separate SFU session.
  // Returns ownSessionId (so CallWindow can signal the peer), or null on failure.
  // ---------------------------------------------------------------------------
  const enableCamera = useCallback(async (): Promise<number | null> => {
    const auth = authRef.current;
    const room = roomRef.current;
    const client = clientRef.current;
    if (!auth || !room || !client) return null;
    // Dedup: camera already on / mid-setup — a double-click or re-render must not
    // open a second publish session (the "6 video ports" leak).
    if (cameraSessionRef.current || cameraPendingRef.current) return null;
    cameraPendingRef.current = true;

    try {
      let raw: MediaStream;
      try {
        // Uses the remembered camera (or the OS default when unset).
        raw = await navigator.mediaDevices.getUserMedia({
          video: deviceConstraint(preferredVideoDeviceIdRef.current),
          audio: false,
        });
      } catch {
        // A remembered camera may have been unplugged → forget it and retry the
        // OS default once before giving up.
        if (isDefaultDeviceId(preferredVideoDeviceIdRef.current)) return null;
        preferredVideoDeviceIdRef.current = null;
        saveDevicePref('videoinput', null);
        try {
          raw = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } catch {
          return null; // permission denied / no camera
        }
      }
      rawCameraStreamRef.current = raw;

      // Apply the chosen background effect. ANY failure (model can't load, no
      // WASM, old webview) → publish the RAW camera. The call must never break
      // because a background effect couldn't initialise.
      let outgoing = raw;
      const mode = backgroundModeRef.current;
      if (isEffectMode(mode)) {
        setBackgroundBusy(true);
        try {
          const proc = await createBackgroundProcessor(raw, mode);
          if (proc) {
            bgProcessorRef.current = proc;
            outgoing = proc.stream;
          }
        } finally {
          setBackgroundBusy(false);
        }
      }
      cameraTracksRef.current = outgoing.getVideoTracks();
      setLocalCameraStream(outgoing);
      // Record what's actually realised: the effect if a processor was built,
      // else 'none' (we fell back to the raw camera).
      appliedBgModeRef.current = bgProcessorRef.current ? mode : 'none';

      // True if the publish leg failed/ended terminally (after placeCall's
      // retries) before we record it — then the peer would see black. Don't claim
      // camera-on or store the dead session; return null so the caller toasts the
      // failure and the user can retry the toggle.
      let legFailed = false;
      const session = await client.shareCamera(
        {
          ownSessionId: auth.sessionId,
          meetingId: auth.meetingId,
          conferenceId: auth.conferenceId,
          participantPin: room.participantPin,
          displayName: displayNameRef.current,
          cameraStream: outgoing,
        },
        {
          onRemoteStream: () => { /* publisher: no remote stream expected */ },
          onEnded: () => { legFailed = true; stopCameraMedia(); setLocalCameraStream(null); setCameraOn(false); },
          onFailure: () => { legFailed = true; stopCameraMedia(); setLocalCameraStream(null); setCameraOn(false); },
        },
      );

      if (legFailed || selfHangupRef.current) {
        // Publish failed terminally, OR the call ended while this leg was setting
        // up (retry widened that window) — tear it down; don't claim camera-on.
        session.hangup();
        if (selfHangupRef.current) stopCameraMedia();
        return null;
      }

      cameraSessionRef.current = session;
      setCameraOn(true);

      // Camera unplugged → tear the publish leg down. Watch the RAW device track
      // (a processed canvas track never fires 'ended' on a device unplug).
      raw.getVideoTracks().forEach((t) => {
        t.onended = () => {
          cameraSessionRef.current?.hangup();
          cameraSessionRef.current = null;
          stopCameraMedia();
          setLocalCameraStream(null);
          setCameraOn(false);
        };
      });

      return auth.sessionId;
    } finally {
      cameraPendingRef.current = false;
    }
  }, [stopCameraMedia]);

  // ---------------------------------------------------------------------------
  // disableCamera — tear down the published camera session.
  // ---------------------------------------------------------------------------
  const disableCamera = useCallback(() => {
    cameraSessionRef.current?.hangup();
    cameraSessionRef.current = null;
    stopCameraMedia();
    setLocalCameraStream(null);
    setCameraOn(false);
  }, [stopCameraMedia]);

  // ---------------------------------------------------------------------------
  // reconcileBackground — drive the live published track toward
  // `backgroundModeRef.current`. Single-flight + a re-check loop so rapid
  // switching coalesces (the LATEST desired mode wins) and never spawns two
  // segmentation processors at once. Returns 'unsupported' if building an effect
  // failed (we fall back to the raw camera). Each transition advances
  // `appliedBgModeRef`, so the loop terminates once realised === desired.
  // ---------------------------------------------------------------------------
  const reconcileBackground = useCallback(async (): Promise<'ok' | 'unsupported'> => {
    // A reconcile is already running — it reads `backgroundModeRef` each pass,
    // so it will pick up the newest desired mode without a second processor.
    if (bgReconcilingRef.current) return 'ok';
    bgReconcilingRef.current = true;
    setBackgroundBusy(true);
    let result: 'ok' | 'unsupported' = 'ok';
    try {
      for (;;) {
        const session = cameraSessionRef.current;
        const raw = rawCameraStreamRef.current;
        if (!session || !raw) {
          // Camera off → nothing live; the choice applies on the next enable.
          appliedBgModeRef.current = backgroundModeRef.current;
          return result;
        }
        const desired = backgroundModeRef.current;
        // Settled? (realised matches, and the realisation is consistent).
        if (appliedBgModeRef.current === desired && (desired === 'none' || bgProcessorRef.current)) {
          return result;
        }

        const proc = bgProcessorRef.current;

        // Effect → off: republish the raw camera, tear the processor down.
        if (!isEffectMode(desired)) {
          if (proc) {
            const rawTrack = raw.getVideoTracks()[0];
            try {
              if (rawTrack) await session.replaceTrack?.(rawTrack);
            } catch {
              /* keep the current track rather than leaving it inconsistent */
            }
            proc.stop();
            bgProcessorRef.current = null;
            cameraTracksRef.current = raw.getVideoTracks();
            setLocalCameraStream(raw);
          }
          appliedBgModeRef.current = 'none';
          continue;
        }

        // Effect ↔ effect (processor running): swap the mode in place — no
        // renegotiation, the published canvas track is unchanged.
        if (proc) {
          await proc.setMode(desired);
          appliedBgModeRef.current = desired;
          continue;
        }

        // Raw → effect: build ONE processor over the live raw camera.
        const created = await createBackgroundProcessor(raw, desired);
        // The user may have switched again during the (slow first) build.
        if (backgroundModeRef.current !== desired) {
          created?.stop();
          continue; // apply the newest desired mode
        }
        if (!created) {
          // Couldn't initialise → stay on the raw camera, report it.
          backgroundModeRef.current = 'none';
          setBackgroundModeState('none');
          saveBackgroundPref('none');
          appliedBgModeRef.current = 'none';
          result = 'unsupported';
          continue;
        }
        // The camera was torn down, or a processor appeared, during the build.
        if (cameraSessionRef.current !== session || bgProcessorRef.current) {
          created.stop();
          continue;
        }
        const outTrack = created.stream.getVideoTracks()[0];
        try {
          if (outTrack) await session.replaceTrack?.(outTrack);
        } catch {
          created.stop();
          appliedBgModeRef.current = 'none';
          continue;
        }
        bgProcessorRef.current = created;
        cameraTracksRef.current = created.stream.getVideoTracks();
        setLocalCameraStream(created.stream);
        appliedBgModeRef.current = desired;
        continue;
      }
    } finally {
      bgReconcilingRef.current = false;
      setBackgroundBusy(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // setBackgroundMode — record + persist the desired background, then reconcile
  // the live camera toward it. Safe with the camera off (applies on next
  // enable). Returns 'unsupported' if an effect was requested but couldn't
  // initialise (we stay on the raw camera).
  // ---------------------------------------------------------------------------
  const setBackgroundMode = useCallback(
    async (next: BackgroundMode): Promise<'ok' | 'unsupported'> => {
      backgroundModeRef.current = next;
      setBackgroundModeState(next);
      saveBackgroundPref(next);
      return reconcileBackground();
    },
    [reconcileBackground],
  );

  // ---------------------------------------------------------------------------
  // viewCamera — receive-only subscribe to the peer's published camera.
  // ---------------------------------------------------------------------------
  const viewCamera = useCallback(async (sharerSessionId: number) => {
    const auth = authRef.current;
    const room = roomRef.current;
    const client = clientRef.current;
    if (!auth || !room || !client) return;
    // Never subscribe to our OWN camera (a stale roster could list us).
    if (sharerSessionId === auth.sessionId) return;
    // Dedup: already viewing this sid or a view of it is mid-setup.
    if (viewCameraSessionsRef.current.has(sharerSessionId) || viewCameraPendingRef.current.has(sharerSessionId)) return;
    viewCameraPendingRef.current.add(sharerSessionId);

    const dropCamera = (why: string): void => {
      console.info(`[call] camera view DROPPED sid=${sharerSessionId} (${why})`);
      viewCameraSessionsRef.current.delete(sharerSessionId);
      setRemoteCameras((prev) => {
        if (!prev.has(sharerSessionId)) return prev;
        const next = new Map(prev);
        next.delete(sharerSessionId);
        return next;
      });
    };

    console.info(`[call] camera view SUBSCRIBE sid=${sharerSessionId}`);
    // True if the leg failed/ended terminally (after placeCall's own retries) by
    // the time we'd record it — then we must NOT store the dead session, or
    // syncCameraViews would see the sid as "already viewing" and never retry it
    // (the "LEG UP after DROPPED" no-retry bug). Leaving the maps clean lets the
    // next reconcile tick re-subscribe.
    let legFailed = false;
    try {
      const session = await client.viewCamera(
        {
          sharerSessionId,
          ownSessionId: auth.sessionId,
          meetingId: auth.meetingId,
          conferenceId: auth.conferenceId,
          participantPin: room.participantPin,
          displayName: displayNameRef.current,
        },
        {
          onRemoteStream: (s) => { console.info(`[call] camera view STREAM arrived sid=${sharerSessionId}`); setRemoteCameras((prev) => new Map(prev).set(sharerSessionId, s)); },
          onEnded: () => { legFailed = true; dropCamera('ended'); },
          onFailure: () => { legFailed = true; dropCamera('failed'); },
        },
      );

      if (legFailed || selfHangupRef.current) {
        // Failed terminally, OR the call was torn down while this leg was still
        // setting up (the retry widened that window) — never record it, or we'd
        // leak an SFU leg into a map hangup() already cleared.
        console.info(`[call] camera view NOT RECORDED sid=${sharerSessionId} (${legFailed ? 'failed — retry on next sync' : 'call ended during setup'})`);
        session.hangup();
      } else {
        viewCameraSessionsRef.current.set(sharerSessionId, session);
        console.info(`[call] camera view LEG UP sid=${sharerSessionId}`);
      }
    } finally {
      viewCameraPendingRef.current.delete(sharerSessionId);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // stopViewCamera — tear down ONE remote camera view leg (by sid).
  // ---------------------------------------------------------------------------
  const stopViewCamera = useCallback((sharerSessionId: number) => {
    viewCameraPendingRef.current.delete(sharerSessionId);
    viewCameraSessionsRef.current.get(sharerSessionId)?.hangup();
    viewCameraSessionsRef.current.delete(sharerSessionId);
    setRemoteCameras((prev) => {
      if (!prev.has(sharerSessionId)) return prev;
      const next = new Map(prev);
      next.delete(sharerSessionId);
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // syncCameraViews — reconcile the set of remote cameras we're viewing to the
  // DESIRED set (every joined peer currently publishing). Opens a view leg for
  // each new sid and tears down legs for sids no longer desired. This is the
  // all-to-all driver for group calls (and subsumes the 1:1 single-peer path).
  // ---------------------------------------------------------------------------
  const syncCameraViews = useCallback((desiredSids: readonly number[]) => {
    const desired = new Set(desiredSids);
    console.info(`[call] syncCameraViews desired=[${[...desired]}] viewing=[${[...viewCameraSessionsRef.current.keys()]}]`);
    // Tear down views we no longer want.
    for (const sid of viewCameraSessionsRef.current.keys()) {
      if (!desired.has(sid)) { console.info(`[call] syncCameraViews TEAR DOWN sid=${sid} (not desired)`); stopViewCamera(sid); }
    }
    // Open views we want but don't have yet.
    for (const sid of desired) {
      if (!viewCameraSessionsRef.current.has(sid)) void viewCamera(sid);
    }
  }, [viewCamera, stopViewCamera]);

  // ---------------------------------------------------------------------------
  // toggleMute — mute/unmute the local mic (the base call's audio track). Just
  // flips track.enabled (no renegotiation), so it's instant + reversible.
  // ---------------------------------------------------------------------------
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      localStreamRef.current?.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // switchAudioInput / switchVideoInput — change the mic / camera device live
  // by hot-swapping the sending track (replaceTrack — no renegotiation).
  // ---------------------------------------------------------------------------
  const switchAudioInput = useCallback(async (deviceId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false,
      });
      const newTrack = stream.getAudioTracks()[0];
      if (!newTrack) return;
      // Preserve the current mute state on the new track.
      const wasEnabled = localStreamRef.current?.getAudioTracks()[0]?.enabled ?? true;
      newTrack.enabled = wasEnabled;
      await sessionRef.current?.replaceTrack?.(newTrack);
      localStreamRef.current?.getAudioTracks().forEach((t) => t.stop());
      localStreamRef.current = stream;
      setLocalAudioStream(stream);
      // Remember the manual choice for the next call (the device worked).
      preferredAudioDeviceIdRef.current = deviceId;
      saveDevicePref('audioinput', deviceId);
    } catch {
      /* device unavailable / denied — keep the current mic */
    }
  }, []);

  const switchVideoInput = useCallback(async (deviceId: string) => {
    // Remember the manual choice immediately — even with the camera off it must
    // apply the next time it turns on (and persist across calls).
    preferredVideoDeviceIdRef.current = deviceId;
    saveDevicePref('videoinput', deviceId);
    if (!cameraSessionRef.current) return; // camera off — applies next time it turns on
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });
      const newTrack = stream.getVideoTracks()[0];
      if (!newTrack) return;

      const proc = bgProcessorRef.current;
      if (proc) {
        // A background effect is active → feed the new camera into the processor.
        // The published canvas track is unchanged (no renegotiation, no rebuild
        // of the segmenter). Swap the raw source + retarget the unplug handler.
        proc.setSource(stream);
        rawCameraStreamRef.current?.getTracks().forEach((t) => {
          t.onended = null;
          t.stop();
        });
        rawCameraStreamRef.current = stream;
        newTrack.onended = () => disableCamera();
        return;
      }

      // No effect → hot-swap the sending track directly (replaceTrack).
      await cameraSessionRef.current.replaceTrack?.(newTrack);
      // Clear the old tracks' onended BEFORE stopping them — their handler (from
      // enableCamera) hangs up the camera session, which we are KEEPING (we only
      // swapped the track). Stopping with the handler live would kill the camera.
      cameraTracksRef.current.forEach((t) => {
        t.onended = null;
        t.stop();
      });
      cameraTracksRef.current = stream.getVideoTracks();
      rawCameraStreamRef.current = stream;
      setLocalCameraStream(stream);
      newTrack.onended = () => disableCamera();
    } catch {
      /* device unavailable / denied — keep the current camera */
    }
  }, [disableCamera]);

  // ---------------------------------------------------------------------------
  // hangup — end main call + clean up screen + camera sessions. Stops EVERY
  // local track and terminates EVERY SFU session (each ua.stop()s its socket).
  // ---------------------------------------------------------------------------
  const hangup = useCallback(() => {
    selfHangupRef.current = true;

    // Tear down screen share
    screenShareSessionRef.current?.hangup();
    screenShareSessionRef.current = null;
    stopScreenTracks();
    setIsSharing(false);
    sharingSessionIdRef.current = null;

    // Tear down screen view
    viewSessionRef.current?.hangup();
    viewSessionRef.current = null;
    setRemoteScreenStream(null);

    // Tear down camera publish + view (stops the processor + raw + canvas track)
    cameraSessionRef.current?.hangup();
    cameraSessionRef.current = null;
    stopCameraMedia();
    setLocalCameraStream(null);
    setCameraOn(false);
    for (const s of viewCameraSessionsRef.current.values()) s.hangup();
    viewCameraSessionsRef.current.clear();
    viewCameraPendingRef.current.clear();
    setRemoteCameras(new Map());

    // Tear down main call
    sessionRef.current?.hangup();
    sessionRef.current = null;
    stopLocalStream();
    setLocalAudioStream(null);
    setRemoteStream(null);
    setConnectionState(null);
    setMuted(false);
    setWebcastToken(null);
    setState('ended');
  }, [stopLocalStream, stopScreenTracks, stopCameraMedia]);

  return {
    state,
    connectionState,
    remoteStream,
    localAudioStream,
    muted,
    isSharing,
    localScreenStream,
    remoteScreenStream,
    cameraOn,
    localCameraStream,
    // All remote cameras keyed by publisher sid (group grid renders per-sid).
    remoteCameras,
    // Back-compat single remote camera for the 1:1 VideoStage (at most one entry).
    remoteCameraStream: remoteCameras.size > 0 ? [...remoteCameras.values()][0]! : null,
    backgroundMode,
    backgroundBusy,
    webcastToken,
    joinRoom,
    joinConsultation,
    hangup,
    toggleMute,
    switchAudioInput,
    switchVideoInput,
    setBackgroundMode,
    startScreenShare,
    stopScreenShare,
    viewScreen,
    stopViewScreen,
    enableCamera,
    disableCamera,
    viewCamera,
    stopViewCamera,
    syncCameraViews,
  };
}
